/**
 * "Can Omni do this?" — the answer panel above the guide search results.
 *
 * A service desk operator types what the caller wants in their own words ("Dave
 * needs access to the Finance share", "can't get on the remote desktop") and
 * gets back: whether Omni does it, the screen that does it with a button
 * straight there, and the steps — each traceable to the guide it came from.
 *
 * Everything shown is grounded in the guides. When the answer is keyword-only
 * (AI off or unreachable) that is stated rather than dressed up, because steps
 * are the part an operator would act on and a guess is worse than nothing.
 */

import { useEffect, useRef, useState } from 'react';
import { assistHelp } from '@/api/endpoints/help.js';
import { tabForHelpSection, useHelpStore } from '@/state/helpStore.js';
import { isValidTab, useUiStore } from '@/state/uiStore.js';

export default function AskOmni({ query }) {
  const [answer, setAnswer] = useState(null);
  const [loading, setLoading] = useState(false);
  const requestRef = useRef(0);
  const setActiveTab = useUiStore((s) => s.setActiveTab);
  const closeHelp = useHelpStore((s) => s.close);
  const goToArticle = useHelpStore((s) => s.goToArticle);

  useEffect(() => {
    const q = (query || '').trim();
    if (q.length < 8) {
      // Below this it's a keyword, not a question — the results list is a better
      // answer than a paragraph, so stay out of the way.
      setAnswer(null);
      setLoading(false);
      return;
    }
    const id = ++requestRef.current;
    setLoading(true);
    assistHelp(q)
      .then((r) => {
        if (id !== requestRef.current) return;
        setAnswer(r?.answer || null);
      })
      .catch(() => {
        if (id === requestRef.current) setAnswer(null);
      })
      .finally(() => {
        if (id === requestRef.current) setLoading(false);
      });
  }, [query]);

  if (loading && !answer) {
    return (
      <div className="help-assist help-assist--thinking">
        <i className="fas fa-wand-magic-sparkles help-spin" aria-hidden="true" />
        <span>Looking through the guides…</span>
      </div>
    );
  }
  if (!answer || !answer.answer) return null;

  const targetTab = answer.section ? tabForHelpSection(answer.section) : null;
  const canNavigate = Boolean(targetTab && isValidTab(targetTab));

  return (
    <div className={`help-assist${answer.canDoInOmni ? '' : ' help-assist--none'}`}>
      <div className="help-assist-head">
        <i
          className={`fas ${answer.canDoInOmni ? 'fa-wand-magic-sparkles' : 'fa-circle-info'}`}
          aria-hidden="true"
        />
        <strong>{answer.canDoInOmni ? 'Omni can do this' : "Omni doesn't cover this"}</strong>
        {answer.sectionTitle && <span className="help-assist-where">in {answer.sectionTitle}</span>}
        {loading && <i className="fas fa-spinner help-spin" aria-hidden="true" style={{ opacity: 0.6 }} />}
        {canNavigate && (
          <button
            type="button"
            className="help-mini-btn help-assist-go"
            onClick={() => {
              setActiveTab(targetTab);
              closeHelp();
            }}
          >
            Take me there <i className="fas fa-arrow-right" aria-hidden="true" />
          </button>
        )}
      </div>

      <p className="help-assist-answer">{answer.answer}</p>

      {answer.steps?.length > 0 && (
        <ol className="help-assist-steps">
          {answer.steps.map((step, i) => (
            <li key={i}>
              {step.text}
              {/* The source guide, so a step can be checked rather than trusted. */}
              {step.articleId && (
                <button
                  type="button"
                  className="help-assist-cite"
                  onClick={() => goToArticle(step.articleId, answer.section)}
                  title={`From: ${step.articleTitle}`}
                >
                  <i className="fas fa-book-open" aria-hidden="true" /> {step.articleTitle}
                </button>
              )}
            </li>
          ))}
        </ol>
      )}

      {answer.articles?.length > 0 && (
        <div className="help-assist-refs">
          <span>Full guides:</span>
          {answer.articles.map((a) => (
            <button key={a.id} type="button" className="help-mini-btn" onClick={() => goToArticle(a.id, a.section)}>
              {a.title}
            </button>
          ))}
        </div>
      )}

      {answer.degradedReason && (
        <p className="help-assist-note">
          <i className="fas fa-triangle-exclamation" aria-hidden="true" /> {answer.degradedReason} No steps are shown
          because they would be guesswork.
        </p>
      )}
    </div>
  );
}
